import { Dialog } from '@headlessui/react'
import buttonStyles from '@/styles/Buttons.module.scss'
import styles from './TwoFactorModal.module.css'
import classNames from 'classnames'
import waitlistStyles from '@/styles/Waitlist.module.scss'
import {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  ReactNode,
} from 'react'
import { CircularProgress } from '@mui/material'

export interface TwoFactorModalProps {
  children: ReactNode
  codeCharacterCount?: number
  isOpen: boolean
  title?: string
  helperText?: string
  isError?: boolean
  isLoadingGuestData: boolean
  handleClose?: () => void
  requestCode?: () => void
  handleVerify?: (code: string, setVerify: (arg: string) => void) => Promise<number>
}

const TwoFactorModal = ({
  children,
  codeCharacterCount = 6,
  isOpen,
  title,
  helperText,
  isError,
  isLoadingGuestData,
  handleClose,
  requestCode,
  handleVerify,
}: TwoFactorModalProps) => {
  const [verifyCode, setVerifyCode] = useState('')
  const maxCode = useMemo(() => {
    return Array(codeCharacterCount).fill('9').join('')
  }, [codeCharacterCount]) 
  const inputRef = useRef<HTMLInputElement>(null)

  const canSubmit = useMemo(() => {
    return verifyCode?.toString()?.length === codeCharacterCount
  }, [verifyCode])

  const handleCodeInputChange = useCallback(
    (event) => {
      const value = event?.target?.value
      setVerifyCode(value)
    },
    [verifyCode, codeCharacterCount]
  )

  const handleCodeSubmit = useCallback(async () => {
    const authCode = verifyCode?.toString()
    const verifyStatus = await handleVerify(authCode, setVerifyCode)

  }, [verifyCode])

  useEffect(() => {
    if (isOpen) {
      requestCode()
    }
  }, [isOpen])

  return (
    <Dialog
      open={isOpen}
      onClose={() => handleClose()}
      className="relative z-50"
    >
      {/** Backdrop */}
      <div
        onClick={() => handleClose()}
        className="backdrop fixed inset-0 bg-black/30"
        aria-hidden="true"
      />

      {/** Full screen container */}
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <Dialog.Panel className="mx-auto w-[520px] max-w-[calc(100vw-40px)] rounded-2xl bg-white flex flex-col gap-[20px] p-[40px]">
          <button 
            onClick={() => handleClose()}
            className="self-end"
          >
            <img src="/icons/close.svg" alt="close" />
          </button>
          <Dialog.Title className={styles['dialog-title']}>
            {title}
          </Dialog.Title>
          <div className="dialog-content">{children}</div>
          <div className="verify-container max-w-full">
            <div className="verify-form flex flex-wrap justify-end gap-[12px]">
              <div className={styles['input-container']}>
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={codeCharacterCount}
                  value={verifyCode}
                  autoComplete={'one-time-code'}
                  className={classNames(styles['input'])}
                  onChange={(e) => {
                    handleCodeInputChange(e)
                  }}
                />

              </div>
              <div className="resend-btn">
                {/** Resend btn */}
                <button
                  tabIndex={codeCharacterCount + 1}
                  className={styles['textlink']}
                  onClick={() => {
                    requestCode()
                    // reset fields
                  }}
                >
                  Resend code
                </button>
              </div>
            </div>
          </div>
          <button
            id="verify-2fa-btn"
            data-test-id="verify-2fa-btn"
            tabIndex={codeCharacterCount}
            className={classNames(
              buttonStyles.cardBtn,
              buttonStyles.blueBtn,
              'btn mt-md-0 !w-full p-2',
              !canSubmit
                ? 'opacity-50 cursor-not-allowed pointer-events-none'
                : null
            )}
            style={{ fontSize: '1rem' }}
            onClick={() => handleCodeSubmit()}
            type="button"
            disabled={!canSubmit}
          >
            {isLoadingGuestData ? (
              <CircularProgress
                size={20}
                className={classNames(waitlistStyles.buttonLoadingSpinner)}
              />
            ) : (
              <>Verify</>
            )}
          </button>
          <div className="footer mt-[-10px]">
            {/** HelperText: error, info */}
            <p
              className={classNames(
                styles['helper-text'],
                isError ? styles['error'] : null
              )}
            >
              {helperText}
            </p>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default TwoFactorModal
